import { AccordionDetails, Grid } from '@mui/material'
import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useGetMeasureUnitsQuery } from '../../../app/redux-fetch/apiCoreQuery'
import {
  useGetAllLoadTypesQuery,
  useGetCurrenciesByCustomerIdQuery,
} from '../../../app/redux-fetch/apiOrders'
import { convertToLabelValue } from '../../../app/utils'
import StyledAccordion from '../../../components/Accordions/StyledAccordion'
import StyledAccordionSummary from '../../../components/Accordions/StyledAccordionSummary'
import ActionButton from '../../../components/Buttons/ActionButton'
import { useConfirmDialogContext } from '../../../components/ConfirmDialog'
import DefaultSpacingGrid from '../../../components/Containers/DefaultSpacingGrid'
import FormInput from '../../../components/Forms/FormInput'
import FormSelect from '../../../components/Forms/FormSelect'
import commonMessages from '../../../components/Messages/commonMessages'
import { LoadType, LocationType } from '../../../types/common'
import { useOrderFormContext } from '../context/OrderFormContext'
import { useLocationModal } from '../hooks/useLocationModal'
import { useOrderStatus } from '../hooks/useOrderStatus'
import messages from './messages'
import { getLocationRevision } from './utils'

interface ConsignmentInformationFormProps {
  index: number
  disabled?: boolean
}

const ConsignmentInformationForm: React.FC<ConsignmentInformationFormProps> = ({
  index,
  disabled = false,
}: ConsignmentInformationFormProps) => {
  const { watch, setValue, unregister } = useFormContext()
  const { formatMessage } = useIntl()
  const [expanded, setExpanded] = useState(true)
  const { variant } = useOrderFormContext()
  const { status } = useOrderStatus()
  const { confirm } = useConfirmDialogContext()

  const customerId = watch('customerId')
  const isNew = watch(`bookings.${index}.isNew`)
  const currentLoadTypeId = watch(`bookings.${index}.loadTypeId`)
  const packaging = watch(`bookings.${index}.packaging`) || []

  const hasLoads = packaging.length > 0

  const shouldDisableConsignmentType = disabled || (status && !isNew)

  const handleToggleAccordion = () => {
    setExpanded((prev) => !prev)
  }

  const { data: currencies = [] } = useGetCurrenciesByCustomerIdQuery({
    customerId: customerId ?? 0,
    hideDisabled: true,
  }, { skip: disabled })

  const { data: measureUnits = [] } = useGetMeasureUnitsQuery(undefined, { skip: disabled })

  const { data: packagingTypes } = useGetAllLoadTypesQuery(undefined, { skip: disabled })

  const massUnitOptions = useMemo(
    () =>
      convertToLabelValue(
        measureUnits
          .filter((x) => x.measureTypeId === 1)
          .map((x) => {
            return { name: x.units, id: x.id }
          })
      ),
    [measureUnits]
  )

  const lengthUnitOptions = useMemo(
    () =>
      convertToLabelValue(
        measureUnits
          .filter((x) => x.measureTypeId === 4)
          .map((x) => {
            return { name: x.units, id: x.id }
          })
      ),
    [measureUnits]
  )

  const resetFields = (fields: any) => {
    const loads = watch(`bookings.${index}.packaging`) || []
    loads.forEach((_: any, idx: number) => {
      fields.forEach((field: any) => {
        setValue(`bookings.${index}.packaging.${idx}.${field}`, undefined)
      })
    })

    const waypoints = watch(`bookings.${index}.waypoints`) || []

    waypoints.forEach((_: any, idx: number) => {
      setValue(`bookings.${index}.waypoints.${idx}.services`, [])
    })

    setValue(`bookings.${index}.shipFrom.services`, [])
    setValue(`bookings.${index}.deliverTo.services`, [])
  }

  const handlePackagingTypeChange = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newPackagingTypeId = Number(event.target.value)

    if (isNew && hasLoads && newPackagingTypeId !== currentLoadTypeId) {
      const confirmed = await confirm({
        title: formatMessage(messages.changeConsignmentTypeTitle),
        message: formatMessage(messages.changeConsignmentTypeMessage),
        confirmText: formatMessage(messages.yes),
        cancelText: formatMessage(messages.no),
        severity: 'warning',
      })

      if (confirmed) {
        setValue(`bookings.${index}.packaging`, [])
        applyLoadTypeChange(newPackagingTypeId)
      } else {
        setValue(`bookings.${index}.loadTypeId`, currentLoadTypeId)
      }
      return
    }

    applyLoadTypeChange(newPackagingTypeId)
  }

  const applyLoadTypeChange = (newPackagingTypeId: number) => {
    setValue(`bookings.${index}.loadTypeId`, newPackagingTypeId)
    const selectedLoadType = packagingTypes?.find((t: any) => t.id === newPackagingTypeId)
    setValue(`bookings.${index}.loadType`, selectedLoadType ?? undefined)
    const fieldsToReset = [
      'packagingTemplateId',
      'containerType',
      'weight',
      'weightUnit',
      'length',
      'width',
      'height',
      'dimensionUnit',
    ]
    resetFields(fieldsToReset)
    if (newPackagingTypeId === LoadType.parcel) {
      const packagings = watch(`bookings.${index}.packaging`) || []
      packagings.forEach((_: any, idx: number) => {
        setValue(`bookings.${index}.packaging.${idx}.weightUnit`, massUnitOptions[0]?.value ?? 0)
        setValue(
          `bookings.${index}.packaging.${idx}.dimensionUnit`,
          lengthUnitOptions[0]?.value ?? 0
        )
      })
    }
    unregister(`bookings.${index}.containerCollections`)
    setValue(`bookings.${index}.shipFrom.emptyContainerCollection`, false)
  }

  const oldLocations = watch(`bookings.${index}.shipper`)

  const { ModalComponent, locationOptions, locations, openModal } = useLocationModal({
    onSave: (id) => {
      setValue(`bookings.${index}.shipper`, id)
    },
    customerId,
    locationTypeId: LocationType.Shipper,
    oldLocations: oldLocations ? [oldLocations] : [],
    disabled,
  })

  return (
    <>
      {ModalComponent}
      <StyledAccordion
        sx={{
          '&:before': {
            display: 'none',
          },
        }}
        expanded={expanded}
        onChange={handleToggleAccordion}
      >
        <StyledAccordionSummary
          index={`consignment-info-content-${index}`}
          title='Consignment Information'
          expanded={expanded}
        />
        <AccordionDetails>
          {variant === 'summary' ? (
            <></>
          ) : (
            <DefaultSpacingGrid container>
              <Grid item xs={12} md={3}>
                <FormSelect
                  name={`bookings.${index}.loadTypeId`}
                  label={formatMessage(messages.consignmentType)}
                  options={convertToLabelValue(packagingTypes ?? [])}
                  placeholder={formatMessage(commonMessages.select)}
                  required={true}
                  onChange={handlePackagingTypeChange}
                  fullWidth
                  disabled={shouldDisableConsignmentType}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormSelect
                  name={`bookings.${index}.shipperId`}
                  label={formatMessage(messages.shipper)}
                  options={locationOptions}
                  allOptions={locationOptions}
                  required={true}
                  onChange={(e) => {
                    const selectedId = +e.target.value
                    const revision = getLocationRevision(locations, selectedId)
                    setValue(`bookings.${index}.shipperRevision`, revision)
                    const selectedLocation = locations.find((l: any) => l.id === selectedId)
                    setValue(`bookings.${index}.shipper`, selectedLocation ?? undefined)
                  }}
                  actionLabel={
                    !disabled ? (
                      <ActionButton
                        onClick={() => {
                          openModal()
                        }}
                        label={formatMessage(commonMessages.addNew)}
                      />
                    ) : undefined
                  }
                  fullWidth
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormSelect
                  name={`bookings.${index}.currencyId`}
                  label={formatMessage(messages.currency)}
                  required={true}
                  options={convertToLabelValue(
                    currencies.map((currency) => ({
                      id: currency.id,
                      name: `${currency.iso3} - ${currency.localisedSymbol}`,
                    }))
                  )}
                  onChange={(e) => {
                    const selected = currencies.find((c) => c.id === Number(e.target.value))
                    setValue(`bookings.${index}.currency`, selected ?? undefined)
                  }}
                  fullWidth
                  disabled={shouldDisableConsignmentType}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`bookings.${index}.referenceNumber`}
                  label={formatMessage(messages.consignmentReferenceNumber)}
                  placeholder={formatMessage(commonMessages.pleaseEnter)}
                  fullWidth
                  maxLength={50}
                  inputPattern={/[<>{}]/g}
                  inputPatternMessage={formatMessage(messages.bannedCharactersWarning)}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={9}>
                <FormInput
                  name={`bookings.${index}.description`}
                  label={formatMessage(messages.consignmentDescription)}
                  placeholder={formatMessage(messages.consignmentDescription)}
                  required={true}
                  fullWidth
                  maxLength={200}
                  disabled={disabled}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormInput
                  name={`bookings.${index}.trackingNumber`}
                  label={formatMessage(messages.consignmentTrackingNumber)}
                  placeholder={formatMessage(messages.systemGenerated)}
                  disabled={true}
                  fullWidth
                />
              </Grid>
            </DefaultSpacingGrid>
          )}
        </AccordionDetails>
      </StyledAccordion>
    </>
  )
}

export default ConsignmentInformationForm
